// @greenlight-spec: 20-agent-plugins.md#marketplace-repo-bootstrap
// @greenlight-spec: decisions/0040-multiple-plugin-marketplace-repositories.md

import * as React from 'react';

import { thrownCode, thrownMessage } from '@/lib/apiErrors';
import {
  DEFAULT_PLUGIN_MARKETPLACE_REPO_NAMES,
  PLUGIN_MARKETPLACE_REPO_NAME_PATTERN,
  type BootstrapPluginMarketplaceBody,
  type PluginMarketplaceBootstrapResult,
  type PluginMarketplaceStatus,
  type PluginMarketplaceUnregisterResult,
  type PluginMarketplaceVisibility,
} from '@greenlight/shared';

export type BootstrapMarketplace = (
  input: BootstrapPluginMarketplaceBody,
) => Promise<PluginMarketplaceBootstrapResult>;

export type UnregisterMarketplace = (
  marketplaceId: string,
) => Promise<PluginMarketplaceUnregisterResult>;

export interface MarketplaceEditorState {
  visibility: PluginMarketplaceVisibility;
  setVisibility: (next: PluginMarketplaceVisibility) => void;
  repoName: string;
  setRepoName: (next: string) => void;
  /** Non-empty name that GitHub would rewrite — block create and explain. */
  repoNameInvalid: boolean;
  canCreate: boolean;
  pendingKey: string | null;
  error: string | null;
  /** The server's error `code` for the last failed bootstrap, e.g. to show a
   * distinct recovery action for a stale GitHub App installation. */
  errorCode: string | null;
  create: () => Promise<PluginMarketplaceBootstrapResult | null>;
  reconcile: (
    repository: PluginMarketplaceStatus,
  ) => Promise<PluginMarketplaceBootstrapResult | null>;
  /** The repository staged for the unregister confirm dialog, or null when closed. */
  unregisterTarget: PluginMarketplaceStatus | null;
  requestUnregister: (repository: PluginMarketplaceStatus) => void;
  cancelUnregister: () => void;
  unregisterPending: boolean;
  unregisterError: string | null;
  confirmUnregister: () => Promise<void>;
}

function repoNameOf(repoFullName: string): string {
  return repoFullName.slice(repoFullName.lastIndexOf('/') + 1);
}

/**
 * Form/action state for the shared marketplace editor. The repository list
 * itself stays owned by the caller (wizard step state or the settings query),
 * which refreshes it after every successful bootstrap. `onUnregister` is
 * optional: the install wizard reuses this editor with no unregister
 * affordance (docs/28 — retiring a row is a Settings-only capability), while
 * the Settings surface passes it through.
 */
export function useMarketplaceEditor(
  onBootstrap: BootstrapMarketplace,
  onUnregister?: UnregisterMarketplace,
): MarketplaceEditorState {
  const [visibility, setVisibilityState] = React.useState<PluginMarketplaceVisibility>('private');
  const [repoName, setRepoName] = React.useState<string>(
    DEFAULT_PLUGIN_MARKETPLACE_REPO_NAMES.private,
  );
  const [pendingKey, setPendingKey] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [errorCode, setErrorCode] = React.useState<string | null>(null);

  const setVisibility = (next: PluginMarketplaceVisibility): void => {
    setRepoName((name) =>
      name === DEFAULT_PLUGIN_MARKETPLACE_REPO_NAMES[visibility]
        ? DEFAULT_PLUGIN_MARKETPLACE_REPO_NAMES[next]
        : name,
    );
    setVisibilityState(next);
  };

  const trimmedRepoName = repoName.trim();
  const repoNameInvalid =
    trimmedRepoName.length > 0 && !PLUGIN_MARKETPLACE_REPO_NAME_PATTERN.test(trimmedRepoName);
  const canCreate = pendingKey === null && !repoNameInvalid && trimmedRepoName.length > 0;

  const run = async (
    input: BootstrapPluginMarketplaceBody,
    key: string,
  ): Promise<PluginMarketplaceBootstrapResult | null> => {
    if (pendingKey !== null) return null;
    setPendingKey(key);
    setError(null);
    setErrorCode(null);
    try {
      return await onBootstrap(input);
    } catch (cause) {
      setError(thrownMessage(cause, 'Could not create or reconcile the marketplace repository.'));
      setErrorCode(thrownCode(cause) ?? null);
      return null;
    } finally {
      setPendingKey(null);
    }
  };

  const create = (): Promise<PluginMarketplaceBootstrapResult | null> => {
    if (!canCreate) return Promise.resolve(null);
    return run({ repo_name: trimmedRepoName, visibility }, 'create');
  };

  const reconcile = (
    repository: PluginMarketplaceStatus,
  ): Promise<PluginMarketplaceBootstrapResult | null> =>
    run(
      {
        repo_name: repoNameOf(repository.repo_full_name),
        visibility: repository.visibility,
      },
      repository.repo_full_name,
    );

  const [unregisterTarget, setUnregisterTarget] = React.useState<PluginMarketplaceStatus | null>(
    null,
  );
  const [unregisterPending, setUnregisterPending] = React.useState(false);
  const [unregisterError, setUnregisterError] = React.useState<string | null>(null);

  const requestUnregister = (repository: PluginMarketplaceStatus): void => {
    setUnregisterError(null);
    setUnregisterTarget(repository);
  };

  const cancelUnregister = (): void => {
    if (unregisterPending) return;
    setUnregisterTarget(null);
    setUnregisterError(null);
  };

  const confirmUnregister = async (): Promise<void> => {
    if (!unregisterTarget || !onUnregister || unregisterPending) return;
    setUnregisterPending(true);
    setUnregisterError(null);
    try {
      await onUnregister(unregisterTarget.id);
      setUnregisterTarget(null);
    } catch (cause) {
      setUnregisterError(thrownMessage(cause, 'Could not unregister the marketplace repository.'));
    } finally {
      setUnregisterPending(false);
    }
  };

  return {
    visibility,
    setVisibility,
    repoName,
    setRepoName,
    repoNameInvalid,
    canCreate,
    pendingKey,
    error,
    errorCode,
    create,
    reconcile,
    unregisterTarget,
    requestUnregister,
    cancelUnregister,
    unregisterPending,
    unregisterError,
    confirmUnregister,
  };
}
